import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  Logout,
  AccountCircle,
  ManageAccounts
} from "@mui/icons-material"
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import { getClients, type Client } from '@/api/client.service';
import { getUserCompanies, UserRoles } from '@/api/user.service';

export const Layout = () => {
  const { user, setLoggedClient, logout } = useAuth();
  const navigate = useNavigate();
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangeClient = async (newClient: Client) => {
    setSelectedClient(newClient);
    setLoggedClient(newClient);
    handleMenuClose();
  };

  useEffect(() => {
    const fetchClients = async () => {
      if (!user) return;

      try {
        if (user.role === UserRoles.Admin) {
          const fetched = await getClients({ limit: 1000, offset: 0 });
          setAvailableClients(fetched.clients ?? []);
        } else {
          // Non-admin: get unique clients from their companies
          const companies = await getUserCompanies(user.id);
          if (companies && companies.length > 0) {
            const clientIds = Array.from(new Set(companies.map(c => c.client_id)));
            const allClients = await getClients({ limit: 1000, offset: 0 });
            const userClients = allClients.clients?.filter(c => clientIds.includes(c.id)) ?? [];
            setAvailableClients(userClients);
            setSelectedClient(user.loggedClient ?? userClients[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [user]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Upper Menu / AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1.5, sm: 3 } }}>
          <Link
            to="/dashboard"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Box sx={{ width: 42, height: 42, color: "primary.main" }}>
              <svg
                viewBox="0 0 70 74"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%" }}
              >
                <path d="M53.5 9C60.9913 9.00003 64.4919 13.9884 64.5 14L54.5 24H15.5L5.5 14C5.50811 13.9884 9.00874 9.00003 16.5 9C24 9 28.4999 19 35 19L35.3037 18.9932C41.6416 18.6899 46.1174 9 53.5 9Z" fill="currentColor" />
                <circle cx="35" cy="8" r="8" fill="currentColor" />
                <path d="M41.5 63C41.5 63 40.5 67.5 36.5 70.5C32.5 73.5 26.5 73 26.5 73V40H7C3.13401 40 0 36.866 0 33C0 29.134 3.13401 26 7 26H63C66.866 26 70 29.134 70 33C70 36.866 66.866 40 63 40H41.5V63Z" fill="currentColor" />
                <path d="M43.5 52.5V42H53.5L63.5 52.5C63.5 52.5 57 56.5 52.5 56.5C48 56.5 43.5 52.5 43.5 52.5Z" fill="currentColor" />
                <path d="M24.5 52.5V42H14.5L4.5 52.5C4.5 52.5 11 56.5 15.5 56.5C20 56.5 24.5 52.5 24.5 52.5Z" fill="currentColor" />
              </svg>
            </Box>
          </Link>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                p: 0,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 220,
                    mt: 1.5,
                  },
                },
              }}
            >
              <MenuItem disabled sx={{ color: 'text.primary', fontSize: '0.85rem', fontWeight: 600 }}>
                {user?.name}
              </MenuItem>
              <Divider />

              {user?.role !== UserRoles.Admin && availableClients.length > 1 && [
                <Box key="client-select" sx={{ px: 1.5, py: 1 }}>
                  <Autocomplete
                    options={availableClients}
                    disableClearable
                    value={selectedClient}
                    onChange={(_, newValue) => newValue && handleChangeClient(newValue)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    size="small"
                    renderInput={(params) => (
                      <TextField {...params} label="Cliente" placeholder="Buscar cliente..." />
                    )}
                    slotProps={{
                      paper: {
                        sx: { mt: 0.5 },
                      },
                    }}
                  />
                </Box>,
                <Divider key="client-divider" />,
              ]}

              {user?.role !== UserRoles.Operador && [
                <MenuItem
                  key="edit-profile"
                  onClick={() => {
                    navigate('/usuarios', { state: { editProfileId: user?.id } });
                    handleMenuClose();
                  }}
                >
                  <ManageAccounts sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Editar perfil
                </MenuItem>,
                <Divider key="edit-profile-divider" />,
              ]}
              <MenuItem
                onClick={() => {
                  logout();
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <Logout sx={{ mr: 1, fontSize: '1.2rem' }} />
                Sair
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content Area */}
      <Container component="main" maxWidth={false} sx={{ flexGrow: 1, py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Outlet />
      </Container>
    </Box>
  );
};